import type {
  CustomerFilterValue,
  ResolvedCustomer,
  UsersTableFilters,
  UsersTableRow,
} from './types.ts';

/** Customer-filter value for "no grant and no properties" rows. */
export const UNASSIGNED_CUSTOMER_FILTER = 'unassigned' as const;

/** Everything off: no search, no filters. */
export function defaultUsersTableFilters(): UsersTableFilters {
  return {
    search: '',
    customer: null,
    type: null,
    status: null,
    role: null,
  };
}

/** True when any filter differs from its default, i.e. "Clear" should show. */
export function hasActiveFilters(filters: UsersTableFilters): boolean {
  return filters.search.trim() !== '' ||
    filters.customer !== null ||
    filters.type !== null ||
    filters.status !== null ||
    filters.role !== null;
}

function matchesCustomer(row: UsersTableRow, customer: CustomerFilterValue): boolean {
  if (customer === UNASSIGNED_CUSTOMER_FILTER) {
    return row.customerState.kind === 'unassigned';
  }
  // A `na` row has no customers, so it can never match a customer id — a Metron
  // user with no grant belongs to no customer.
  return row.customers.some((c) => c.id === customer);
}

/**
 * Search haystack: the user's name and email, every resolved customer name and
 * every assigned property name. Property names stay searchable even though they
 * are not a column, so the ten-odd property users who are only findable by
 * property today do not become unreachable.
 */
function matchesSearch(row: UsersTableRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    row.name,
    row.email,
    ...row.customers.map((c) => c.name),
    ...row.propertyNames,
  ];
  return haystack.some((value) => value.toLowerCase().includes(needle));
}

/**
 * The one row predicate. Pure over the derived row model, so every filter
 * dimension is unit-testable without React.
 */
export function matchesFilters(row: UsersTableRow, filters: UsersTableFilters): boolean {
  if (filters.customer !== null && !matchesCustomer(row, filters.customer)) return false;
  if (filters.type !== null && row.type !== filters.type) return false;
  if (filters.status !== null && row.status.key !== filters.status) return false;
  if (filters.role !== null && row.roleState.kind !== filters.role) return false;
  return matchesSearch(row, filters.search);
}

/**
 * The customer dropdown's options: the distinct customers actually present in
 * the given rows, ordered by name. Building it from the resolved set is what
 * makes customers reachable only through property users appear at all.
 */
export function buildCustomerOptions(rows: readonly UsersTableRow[]): ResolvedCustomer[] {
  const byId = new Map<number, ResolvedCustomer>();
  for (const row of rows) {
    for (const customer of row.customers) {
      if (!byId.has(customer.id)) byId.set(customer.id, customer);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
}
